import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  acceptTeamConnection, createBuyerProfile, createProfessionalLicense, createTeamConnection,
  declineTeamConnection, deleteBuyerProfile, deleteProfessionalLicense, endTeamConnection,
  getBuyerProfiles, getProfessionalLicenses, getProfileShares, shareProfileWithAgent, unshareProfileWithAgent, getTeamConnections, LicenseInput, setSigningAuthority, updateBuyerProfile,
} from '../../../api/Team/team';
import { BuyerProfile, ProfileShare, BuyerProfileValues, ProfessionalLicense, TeamConnection } from '../../../utility/team';

const CONNECTIONS_KEY = [ 'teamConnections' ];
const LICENSES_KEY = [ 'professionalLicenses' ];
const profilesKey = (buyerId?: string) => [ 'buyerProfiles', buyerId || 'me' ];

export function useTeamConnections() {
  const query = useQuery({ queryKey: CONNECTIONS_KEY, queryFn: () => getTeamConnections() });
  return { data: (query.data?.data.results as TeamConnection[]) || [], isLoading: query.isLoading };
}

export function useTeamConnectionActions() {
  const queryClient = useQueryClient();
  const onSuccess = () => queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY });

  return {
    invite: useMutation({
      mutationFn: ({ email, role }: { email: string; role: 'agent' | 'buyer' }) => createTeamConnection(email, role),
      onSuccess,
    }),
    accept: useMutation({ mutationFn: (id: string) => acceptTeamConnection(id), onSuccess }),
    decline: useMutation({ mutationFn: (id: string) => declineTeamConnection(id), onSuccess }),
    end: useMutation({ mutationFn: (id: string) => endTeamConnection(id), onSuccess }),
    signing: useMutation({
      mutationFn: ({ id, allowed }: { id: string; allowed: boolean }) => setSigningAuthority(id, allowed),
      onSuccess,
    }),
  };
}

export function useProfessionalLicenses() {
  const query = useQuery({ queryKey: LICENSES_KEY, queryFn: () => getProfessionalLicenses() });
  return { data: (query.data?.data.results as ProfessionalLicense[]) || [], isLoading: query.isLoading };
}

export function useLicenseActions() {
  const queryClient = useQueryClient();
  const onSuccess = () => queryClient.invalidateQueries({ queryKey: LICENSES_KEY });

  return {
    add: useMutation({ mutationFn: (license: LicenseInput) => createProfessionalLicense(license), onSuccess }),
    remove: useMutation({ mutationFn: (id: string) => deleteProfessionalLicense(id), onSuccess }),
  };
}

export function useBuyerProfiles(buyerId?: string) {
  const query = useQuery({ queryKey: profilesKey(buyerId), queryFn: () => getBuyerProfiles(buyerId) });
  return { data: (query.data?.data.results as BuyerProfile[]) || [], isLoading: query.isLoading };
}

export function useBuyerProfileActions(buyerId?: string) {
  const queryClient = useQueryClient();
  const onSuccess = () => queryClient.invalidateQueries({ queryKey: profilesKey(buyerId) });

  return {
    create: useMutation({ mutationFn: (values: BuyerProfileValues) => createBuyerProfile(values, buyerId), onSuccess }),
    update: useMutation({
      mutationFn: ({ id, values }: { id: string; values: BuyerProfileValues }) => updateBuyerProfile(id, values, buyerId),
      onSuccess,
    }),
    remove: useMutation({ mutationFn: (id: string) => deleteBuyerProfile(id, buyerId), onSuccess }),
  };
}

export function useProfileShares(profileId?: string) {
  const key = [ 'profileShares', profileId ];
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: key, queryFn: () => getProfileShares(profileId as string), enabled: Boolean(profileId) });
  const toggle = useMutation({
    mutationFn: ({ agentId, shared }: { agentId: string; shared: boolean }) =>
      shared ? shareProfileWithAgent(profileId as string, agentId) : unshareProfileWithAgent(profileId as string, agentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  return { data: (query.data?.data.results as ProfileShare[]) || [], isLoading: query.isLoading, toggle };
}
